import { getCountryCodeFromGeo } from '@/helpers/getCountryFromGeo';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { countries, type CountryType } from '@heidi/utils/countries';
import { getAvailableDisplayLanguagesForCountry } from '@heidi/utils/i18n';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import type { PersonalizationFormData } from '@heidi/growth';

function findCountryByCode(countryCode: string | null): CountryType | null {
  if (!countryCode) return null;
  return countries.find((country) => country.countryCode === countryCode) ?? null;
}

/**
 * Upgrades the onboarding country default from timezone guess to IP geo once
 * `/api/geo` resolves. Does not overwrite a user-selected country.
 *
 * Pass `hasUserSetCountry` when the form is resuming from persisted progress:
 * the guard is otherwise a fresh ref on every mount, so a refresh would let geo
 * overwrite a country the user had already chosen.
 */
export function useOnboardingGeoCountryDefault(
  setFormData: React.Dispatch<React.SetStateAction<PersonalizationFormData>>,
  options: { hasUserSetCountry?: boolean } = {}
) {
  const hasUserSetCountryRef = useRef(options.hasUserSetCountry ?? false);

  const { data: geoCountryCode } = useQuery({
    queryKey: ['onboarding-geo-country'],
    queryFn: getCountryCodeFromGeo,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!geoCountryCode || hasUserSetCountryRef.current) return;

    const geoCountry = findCountryByCode(geoCountryCode);
    if (!geoCountry) return;

    setFormData((prev) => {
      if (hasUserSetCountryRef.current) return prev;
      if (prev.country?.countryCode === geoCountry.countryCode) return prev;

      const [primaryLang] = getAvailableDisplayLanguagesForCountry(
        geoCountry.countryCode
      );
      return {
        ...prev,
        country: geoCountry,
        jurisdiction: '',
        displayLanguage: primaryLang ?? LanguageCode.en,
      };
    });
  }, [geoCountryCode, setFormData]);

  const markCountryAsUserSet = useCallback(() => {
    hasUserSetCountryRef.current = true;
  }, []);

  return { markCountryAsUserSet };
}
